import { useRef, useState } from "react";
import { usePausableInterval } from "../hooks/usePausableInterval";
import axios from "axios";
import { APP_VERSION } from "../version";

import { API_BASE as API } from "../lib/apiBase";
// B1098 (Eric 2026-09-06) — 30 s → 5 min. Cette sonde ne sert qu'à détecter
// un nouveau déploiement : trois appels par minute et par onglet ouvert
// représentaient l'essentiel du trafic de fond de l'application.
const POLL_INTERVAL_MS = 300000; // 5 min

// B1051 (Eric 2026-09-04) — Exposé pour le debug console uniquement. Le
// script pre-bundle d'index.html ne l'utilise PLUS pour décider d'un
// rechargement (le « version gate » a été supprimé, voir le RCA dans
// backend/routers/version.py).
if (typeof window !== "undefined") {
  window.__KNORIA_BUNDLE_VERSION__ = APP_VERSION;
}

/**
 * Polls /api/version every 30s. When the server version changes during an
 * active session (= a Deploy happened while the user is working), shows a
 * non-blocking top banner offering a manual reload.
 *
 * Vincent bug 2026-05-02: "Il faudrait un message d'alerte quand il y a eu
 * un Deploy durant une session". The previous version silently hard-reloaded
 * the page, which interrupted users mid-edit. We now surface the update
 * explicitly and let the user pick the moment to reload.
 */
export default function VersionChecker() {
  const [newVersion, setNewVersion] = useState(null);
  const reloadingRef = useRef(false);

  const check = async () => {
    // Vincent B667 (Eric 2026-08-05 SPEC STRICK PROD) — Les 4 garde-fous
    // (hidden + uploadActive + inFlight + backoff) sont gérés par le hook
    // `usePausableInterval` — ce callback ne fait que la requête.
    if (reloadingRef.current) return true;
    try {
      const { data } = await axios.get(`${API}/api/version`, {
        headers: { "Cache-Control": "no-cache" },
        timeout: 6000,
        silent: true,
      });
      // B1051 (Eric 2026-09-04) — Ignorer les valeurs sentinelles. Quand
      // `/api/version` renvoyait "unknown" en prod (backend incapable de
      // lire frontend/src/version.js, absent de son image), ce bandeau
      // « Nouvelle version disponible » s'affichait en permanence et à
      // tort. On n'alerte que sur une version réelle et différente.
      const v = data?.version;
      if (v && v !== APP_VERSION && v !== "unknown" && v !== "dev") {
        setNewVersion(v);
      }
      return true;
    } catch {
      return false;
    }
  };

  usePausableInterval(check, POLL_INTERVAL_MS);

  const reload = () => {
    reloadingRef.current = true;
    const t = Date.now();
    const url = window.location.pathname + (window.location.search ? window.location.search + "&" : "?") + `_v=${t}`;
    window.location.replace(url);
  };

  if (!newVersion) return null;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[100] bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg"
      data-testid="version-update-banner"
      role="alert"
    >
      <div className="max-w-6xl mx-auto px-4 py-2.5 flex items-center justify-between gap-3 text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <span aria-hidden>🔄</span>
          <span className="truncate">
            <strong>Nouvelle version disponible.</strong>
            <span className="hidden sm:inline ml-1 opacity-90">
              Un déploiement a eu lieu pendant votre session — rechargez pour obtenir les dernières corrections.
            </span>
          </span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={reload}
            className="px-3 py-1 bg-white text-orange-700 rounded font-semibold text-xs hover:bg-orange-50 transition-colors"
            data-testid="version-reload-btn"
          >
            Recharger
          </button>
          <button
            onClick={() => setNewVersion(null)}
            className="px-2 py-1 text-white/90 hover:text-white text-xs"
            data-testid="version-dismiss-btn"
            title="Fermer (reapparaitra au prochain check)"
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}
